"use client";

import React, { useRef } from "react";
import { usePathname } from "next/navigation";
import { PortableText } from "next-sanity";
import { urlFor } from "@/sanity/lib/image";
import { PortableTextComponents } from "@/sanity/lib/serializers";
import { SanityDocument } from "@sanity/client";
import { Button } from "../ui/button";
import Link from "next/link";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import clsx from "clsx";
import _ from "lodash";
import TransitionLink from "./transition-link-button";
import Image from "next/image";
import ScrollDown from "./scroll-down";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface HeroSectionProps {
  contents: SanityDocument[] | SanityDocument;
  url?: string;
  coverImage?: string;
}

function HeroSection({
  contents,
  url,
  coverImage,
}: Readonly<HeroSectionProps>) {
  const slug = usePathname().replace("/", "");
  const content = _.isArray(contents)
    ? contents.filter((item) =>
        slug.length === 0
          ? item.slug?.current === "home"
          : item.slug?.current === slug,
      )[0]
    : contents;
  const coverSRC = coverImage ?? urlFor(content.image).format("webp").url();
  const heroRef = useRef(null);
  const isHome = _.isEmpty(slug);
  const timeline = gsap.timeline();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useGSAP(
    () => {
      timeline.fromTo(
        "#heroContent",
        { opacity: 0 },
        { duration: 1.5, opacity: 1 },
      );
      if (isHome) {
        timeline.fromTo(
          ".ctas",
          { opacity: 0, scale: 0.5 },
          {
            duration: 0.5,
            opacity: 1,
            stagger: 0.5,
            scale: 1,
            ease: "power4.in",
          },
          -0.2,
        );
      }
    },
    { scope: heroRef, dependencies: [content] },
  );

  return (
    <section
      ref={heroRef}
      className={clsx(
        "relative grid h-dvh w-full place-items-center overflow-clip bg-cover lg:h-[80vh]",
      )}
    >
      <Image
        src={coverSRC}
        alt={content.title ?? "Hero Image"}
        fill
        className="absolute inset-0 object-cover"
        sizes="100vw"
        priority
      />

      <div className="absolute h-full w-full bg-gradient-to-r from-slate-700 to-slate-900 opacity-85"></div>
      <div className="absolute h-full w-full bg-gradient-to-t from-highlighter via-transparent to-transparent opacity-15"></div>
      <article
        id="heroContent"
        className="relative z-10 flex max-w-4xl flex-col gap-10 px-7 text-center text-white opacity-0 md:text-start"
      >
        {content._type === "services" || content._type === "projects" ? (
          <div className="flex flex-col gap-5 text-center">
            <h1 className="text-4xl font-bold md:text-5xl lg:text-6xl">
              {content.title}
            </h1>
            <p className="mt-2 font-Satoshi-Medium text-lg md:text-xl lg:text-2xl">
              {content.description}
            </p>
          </div>
        ) : (
          <PortableText
            value={content.body}
            components={PortableTextComponents}
          />
        )}
        {isHome && (
          <div className="mt-5 flex flex-col items-center gap-2 md:flex-row">
            {isDesktop && !!url ? (
              <>
                <TransitionLink
                  href="/services"
                  label="Explore our services"
                  className="ctas rounded-sm bg-orange-600 p-6 font-Satoshi-Variable text-base text-white transition-all hover:scale-110 hover:bg-orange-700 md:text-base xl:text-xl"
                  variant="default"
                />
                <Button
                  asChild
                  variant="link"
                  className="ctas group flex font-Satoshi-Variable text-base text-white underline decoration-slate-400 decoration-1 underline-offset-4 transition-all hover:underline-offset-8 md:text-base xl:text-xl"
                >
                  <Link href="/contact-us">
                    <span>First consultation at a discount</span>
                    <ArrowRightIcon className="scale-125 transition-all group-hover:translate-x-2" />
                  </Link>
                </Button>
              </>
            ) : (
              <Button
                asChild
                className="ctas rounded-sm bg-orange-600 p-6 text-base text-white"
              >
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={url ?? ""}
                >
                  Book an Online Meeting
                </Link>
              </Button>
            )}
          </div>
        )}
      </article>
      <ScrollDown id="brandIcon" />
    </section>
  );
}

export default HeroSection;
